import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BalanceEditDialog } from "@/components/people/team/BalanceEditDialog";
import { LedgerDialog } from "@/components/people/LedgerDialog";
import { fmtDays } from "@/lib/people";

function Actions({ onEdit, testId }) {
  return (
    <div className="flex justify-end gap-2 text-[11px] font-medium text-[#4A78A8]">
      <button onClick={() => onEdit("entitlement")} className="hover:underline" data-testid={`${testId}-set`}>Set</button>
      <button onClick={() => onEdit("adjust")} className="hover:underline" data-testid={`${testId}-adjust`}>Adjust</button>
      <button onClick={() => onEdit("history")} className="hover:underline" data-testid={`${testId}-history`}>History</button>
    </div>
  );
}

export function BalancesTable({ orgId, meId }) {
  const [year, setYear] = useState(null);
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/workspace/leave/balances?org_id=${orgId}${year ? `&year=${year}` : ""}`).then((r) => setD(r.data));
  useEffect(() => { setD(null); load(); }, [orgId, year]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" />;
  const base = year || d.year;

  return (
    <div data-testid="balances-table">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Select value={String(base)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="balances-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[base - 1, base, base + 1].map((y) => <SelectItem key={y} value={String(y)}>Leave year {y}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-xs text-[#1E2D44]/50">{d.can_edit ? "You can set entitlements and make adjustments (except your own)." : "Read-only — people administrators manage balances."}</p>
      </div>
      {d.types.map((t) => (
        <div key={t.id} className="amden-card mb-4 overflow-hidden p-0">
          <div className="flex items-center gap-2 border-b border-[#E4E8EF] px-5 py-3 text-sm font-semibold text-[#0B1729]">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.colour }} /> {t.name} · {d.year_label}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-[#1E2D44]/45">
                {["Person", "Entitlement", "Adjusted", "Booked", "Pending", "Available", ""].map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {d.people.map((p) => {
                  const b = p.balances[t.id];
                  const tid = `bal-${p.user_id}-${t.id}`;
                  return (
                    <tr key={p.user_id} className="border-t border-[#EEF1F6]" data-testid={tid}>
                      <td className="px-4 py-2.5"><div className="font-medium text-[#0B1729]">{p.name}</div><div className="text-[11px] text-[#1E2D44]/45">{p.day_hours ? `${p.day_hours}h day` : "No work pattern"}</div></td>
                      <td className="px-4 py-2.5">{b.has_entitlement ? fmtDays(b.entitlement, p.day_hours) : <span className="text-[#B87F1E]">Not set</span>}</td>
                      <td className="px-4 py-2.5">{fmtDays(b.adjustments, p.day_hours)}</td>
                      <td className="px-4 py-2.5">{fmtDays(b.booked, p.day_hours)}</td>
                      <td className="px-4 py-2.5">{fmtDays(b.pending, p.day_hours)}</td>
                      <td className={`px-4 py-2.5 font-semibold ${b.available < 0 ? "text-[#C95555]" : "text-[#0B1729]"}`} data-testid={`${tid}-available`}>{fmtDays(b.available, p.day_hours)}</td>
                      <td className="px-4 py-2.5">
                        {d.can_edit && p.user_id !== meId
                          ? <Actions testId={tid} onEdit={(mode) => setEdit({ mode, p, t, b })} />
                          : <div className="flex justify-end"><button onClick={() => setEdit({ mode: "history", p, t, b })} className="text-[11px] font-medium text-[#4A78A8] hover:underline" data-testid={`${tid}-history`}>History</button></div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {edit?.mode === "history" && (
        <LedgerDialog orgId={orgId} userId={edit.p.user_id} name={edit.p.name} type={edit.t} year={base} yearLabel={d.year_label}
          dayHours={edit.p.day_hours} onClose={() => setEdit(null)} />
      )}
      {edit && edit.mode !== "history" && (
        <BalanceEditDialog orgId={orgId} mode={edit.mode} person={edit.p} type={edit.t} year={base} yearLabel={d.year_label} current={edit.b}
          onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      )}
    </div>
  );
}
